import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard, LineChart, Grid3x3, ListChecks, Settings2, LogOut, Zap, Menu, X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { MarketProvider } from "@/lib/MarketContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/strategies", label: "Strategies", icon: Zap, testid: "nav-strategies" },
  { to: "/option-chain", label: "Option Chain", icon: Grid3x3, testid: "nav-option-chain" },
  { to: "/positions", label: "Positions & Orders", icon: ListChecks, testid: "nav-positions" },
  { to: "/settings", label: "Broker Settings", icon: Settings2, testid: "nav-settings" },
];

function SidebarNav({ onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <>
      <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center shrink-0">
          <LineChart className="w-4 h-4 text-black" />
        </div>
        <div className="min-w-0">
          <div className="font-heading font-bold tracking-tight text-base leading-none">
            ALGO<span className="text-blue-400">DESK</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Options Terminal</div>
        </div>
      </div>
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-white/10 text-white border-l-2 border-blue-400"
                    : "text-slate-400 hover:bg-white/5 hover:text-slate-100 border-l-2 border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="text-xs text-slate-500 mb-1">Signed in as</div>
        <div className="text-sm truncate mb-2" data-testid="current-user-email">{user?.email}</div>
        <Button
          data-testid="logout-btn"
          variant="ghost"
          size="sm"
          className="w-full justify-start text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
          onClick={() => {
            logout();
            onNavigate?.();
            nav("/login");
          }}
        >
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </div>
    </>
  );
}

export default function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open on mobile
  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <MarketProvider>
      <div className="min-h-screen bg-[#0A0A0A] text-slate-100 flex flex-col md:flex-row" data-testid="app-layout">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-40 border-b border-white/10 bg-[#0d0d0d] px-3 py-2.5 flex items-center gap-3">
          <button
            type="button"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
            className="p-2 rounded-sm text-slate-300 hover:bg-white/10"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-sm bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center shrink-0">
              <LineChart className="w-3.5 h-3.5 text-black" />
            </div>
            <div className="font-heading font-bold tracking-tight text-sm truncate">
              ALGO<span className="text-blue-400">DESK</span>
            </div>
          </div>
        </header>

        {/* Mobile overlay */}
        {menuOpen && (
          <button
            type="button"
            aria-label="Close menu overlay"
            className="md:hidden fixed inset-0 z-40 bg-black/60"
            onClick={() => setMenuOpen(false)}
          />
        )}

        {/* Sidebar: drawer on phone, fixed on desktop */}
        <aside
          className={`
            fixed md:static inset-y-0 left-0 z-50
            w-[min(16.5rem,85vw)] shrink-0
            border-r border-white/10 bg-[#0d0d0d]
            flex flex-col
            transition-transform duration-200 ease-out
            md:translate-x-0
            ${menuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          `}
        >
          <div className="md:hidden absolute top-3 right-3">
            <button
              type="button"
              aria-label="Close menu"
              className="p-2 rounded-sm text-slate-400 hover:bg-white/10"
              onClick={() => setMenuOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <SidebarNav onNavigate={() => setMenuOpen(false)} />
        </aside>

        <main className="flex-1 min-w-0 overflow-x-hidden w-full">
          <Outlet />
        </main>
      </div>
    </MarketProvider>
  );
}
